import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { hasPermission } from '@app/helpers/permissions';
import {
  FormatText,
  FormContext,
  FormItem,
  FormNumber,
  FormSubmitButton,
  FormSwitch,
  MasterDetailForm,
  Table,
  usePromise,
  Widget,
} from '@digital/ui';
import { Collapse, Icon, Input, InputNumber } from 'antd';

import CurrencyInput from '@app/components/CurrencyInput';

import { GetPreSuprimentos, UpdateAtributoCci, UpdatePreSuprimentos } from '@app/api/configuracoes';
import { InputTag } from '@app/components/InputTag';

const ConfiguracaoPreSuprimentos = () => {
  const [onupdate, setOnupdate] = useState(false);

  const [dados] = usePromise(async () => await GetPreSuprimentos(), [onupdate], {});
  const update = values => {
    UpdatePreSuprimentos(values).then(data => {
      if (data.valid) {
        setOnupdate(!onupdate);
      }
    });
  };

  const saveAtributo = atributo => {
    UpdateAtributoCci(atributo).then(data => {
      if (data.valid) {
        setOnupdate(!onupdate);
      }
    });
  };

  const columns = useMemo(() => {
    let cols = [];

    cols.push({
      title: 'Atributo',
      width: '10%',
      dataIndex: 'atributo',
      render: (text, record) => (
        <Input
          defaultValue={record.atributo}
          onPressEnter={e => saveAtributo({ ...record, atributo: e.currentTarget.value })}
          disabled
        />
      ),
    });

    cols.push({
      title: 'Definição',
      render: (text, record) => (
        <Input
          defaultValue={record.definicao}
          onPressEnter={e => saveAtributo({ ...record, definicao: e.currentTarget.value })}
        />
      ),
    });

    cols.push({
      title: 'Pontos',
      width: '8%',
      render: (text, record) => (
        <InputNumber
          defaultValue={record.pontos}
          onKeyDown={e => {
            if (e.keyCode === 13) {
              saveAtributo({ ...record, pontos: e.currentTarget.value });
            }
          }}
        />
      ),
    });

    return cols;
  }, [dados]);

  const { Panel } = Collapse;
  return (
    <Collapse bordered={false} expandIcon={() => <Icon type="arrows-right_4" />}>
      <Panel key="configuracoesPreSuprimentos" header="Parâmetros Pré-Contratação">
        <MasterDetailForm
          initialValues={dados}
          onSubmit={update}
          validationSchema={Yup.object().shape({
            preSuprimentosMinimoAnaliseGpc: Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .required('O campo deve ser informado'),
            preSuprimentosLimiteInferiorGovernancaAprovacao: Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .required('O campo deve ser informado'),
            preSuprimentosLimiteSuperiorGovernancaAprovacao: Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .required('O campo deve ser informado'),
            preSuprimentosDdiValorMinimo: Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .required('O campo deve ser informado'),
            preSuprimentosDdiDuracao: Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .required('O campo deve ser informado'),
          })}>
          <Widget
            actions={[
              hasPermission(['CONFIGURACAO_PRE_SUPRIMENTOS_EDITAR']) && (
                <FormSubmitButton noIcon type="primary">
                  <FormatText id="ui.commons.save" uppercase />
                </FormSubmitButton>
              ),
            ]}>
            <CurrencyInput label="Mínimo Análise Pré-Contratação" name="preSuprimentosMinimoAnaliseGpc" span={4} />
            <FormSwitch label="Análise de Risco de Privacidade" name="preSuprimentosAnaliseRiscoPrivacidade" span={4} />
            <fieldset>
              <legend>Governança de Aprovação</legend>
              <CurrencyInput
                label="Limite Inferior Gov. Aprovação"
                name="preSuprimentosLimiteInferiorGovernancaAprovacao"
                span={4}
              />
              <CurrencyInput
                label="Limite Superior Gov. Aprovação"
                name="preSuprimentosLimiteSuperiorGovernancaAprovacao"
                span={4}
              />
            </fieldset>
            <fieldset>
              <legend>DDI - Due Diligence de Integridade</legend>
              <CurrencyInput label="DDI - Valor Mínimo" name="preSuprimentosDdiValorMinimo" span={4} />
              <FormNumber label="DDI - Duração em meses" name="preSuprimentosDdiDuracao" span={4} />
              <FormContext.Consumer>
                {({ formProps }) => (
                  <FormItem label="Característica Contratação" name="caracteristicaContratacao">
                    <InputTag 
                      buttonText={'Adicionar Característica de Contratação'}
                      onChange={function (value: string[]): void {
                        if (formProps) {
                          formProps.setFieldValue('caracteristicaContratacao', value)
                        }
                      }}
                      initialValues={dados && dados.caracteristicaContratacao}
                    />
                  </FormItem>
                )}
              </FormContext.Consumer>
            </fieldset>
            <fieldset>
              <legend>Atributos CCI (Índice de Complexidade do Contrato)</legend>
              <Table columns={columns} dataSource={dados.atributosCci} />
            </fieldset>
          </Widget>
        </MasterDetailForm>
      </Panel>
    </Collapse>
  );
};

export default ConfiguracaoPreSuprimentos;
